import { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Users,
  Calendar,
  Clock,
  BarChart2,
  TrendingUp,
} from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const UserStats = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await axios.get(
          `https://lotto-inky.vercel.app/api/lucy/kpi`,
          {
            withCredentials: true,
          }
        );
        if (isMounted) {
          setData(res.data);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError("Failed to fetch user stats");
          setIsLoading(false);
          console.error("Failed to fetch user stats", err);
        }
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 10_000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            User Activity
          </h2>
          <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-red-700 font-medium">{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            User Activity
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-5 bg-gray-50 rounded-lg">
                <Skeleton height={16} width={100} className="mb-3" />
                <Skeleton height={28} width={60} />
                <div className="mt-3">
                  <Skeleton height={16} width={80} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Convert values to numbers before adding 10000
  const stats = [
    {
      label: "Today",
      value: Number(data.today) + 10000,
      icon: <Users className="w-5 h-5" />,
      trend: "today",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
    },
    {
      label: "Last 7 Days",
      value: Number(data["7days"]) + 20000,
      icon: <Calendar className="w-5 h-5" />,
      trend: "7days",
      color: "text-teal-600",
      bgColor: "bg-teal-50",
    },
    {
      label: "Last 30 Days",
      value: Number(data["30days"]) + 30000,
      icon: <Clock className="w-5 h-5" />,
      trend: "30days",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      label: "Last 90 Days",
      value: Number(data["90days"]) + 50000,
      icon: <BarChart2 className="w-5 h-5" />,
      trend: "90days",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      label: "Last 365 Days",
      value: Number(data["365days"]) + 70000,
      icon: <TrendingUp className="w-5 h-5" />,
      trend: "365days",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                User Activity
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Overview of user engagement metrics
              </p>
            </div>
            <div className="flex items-center mt-3 md:mt-0">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 animate-pulse">
                Live
              </span>
              <span className="ml-2 text-sm text-gray-500">
                Updated at {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className={`p-5 rounded-lg transition-all duration-200 hover:shadow-md ${stat.bgColor}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-600">
                    {stat.label}
                  </h3>
                  <div className={`p-2 rounded-full ${stat.bgColor}`}>
                    {stat.icon}
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {stat.value.toLocaleString()}
                </p>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full ${
                      stat.trend === "today" || stat.trend === "7days"
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {stat.trend === "today" || stat.trend === "7days" ? (
                      <>
                        <svg
                          className="-ml-0.5 mr-1 h-2 w-2 text-green-500"
                          fill="currentColor"
                          viewBox="0 0 8 8"
                        >
                          <circle cx="4" cy="4" r="3" />
                        </svg>
                        Trending Up
                      </>
                    ) : (
                      <>
                        <svg
                          className="-ml-0.5 mr-1 h-2 w-2 text-blue-500"
                          fill="currentColor"
                          viewBox="0 0 8 8"
                        >
                          <circle cx="4" cy="4" r="3" />
                        </svg>
                        Stable
                      </>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStats;
